import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
} from 'react'
import { createPortal } from 'react-dom'
import { format } from 'date-fns'
import { useTheme } from '@mui/material/styles'
import MiniCalendar from '@/v2/components/common/MiniCalendar'
import Label from '@/v2/components/common/FieldLabel'
import Typography from '@/v2/components/common/Typography'
import { getDateTimeWithoutTimezone } from '@/v2/utils/convert'
import BasicCalendar from '@/v2/components/common/BasicCalendar'
import ClearIcon from '@mui/icons-material/Clear'
import { getPopoverPosition, isAnchorOffScreen } from './position'

import { Wrapper, FakeInput, ArrowDown, CalendarWrapper } from './styles'

const SelectCalendar = ({
  label,
  value,
  error,
  disabled = false,
  hasOccupancy = true,
  labelFormat = 'MM/dd/yy',
  onChange,
  isBasicCalendar = false,
  outlineColor,
}) => {
  const theme = useTheme()
  const [open, setOpen] = useState(false)
  const [date, setDate] = useState(null)
  const [fakeInputRef, setFakeInputRef] = useState(null) // Reference for positioning
  const [calendarRef, setCalendarRef] = useState(null)
  const [position, setPosition] = useState(null)

  useEffect(() => {
    if (value && typeof value === 'string') {
      setDate(getDateTimeWithoutTimezone(value))
    } else {
      setDate(value)
    }
  }, [value])

  const toggleCalendar = useCallback(() => {
    if (!disabled && fakeInputRef) {
      setOpen(prev => !prev)
    }
  }, [disabled, fakeInputRef])

  const closeCalendar = useCallback(() => {
    setOpen(false)
  }, [])

  const updatePosition = useCallback(() => {
    if (!fakeInputRef || !calendarRef) return
    setPosition(
      getPopoverPosition({
        anchorRect: fakeInputRef.getBoundingClientRect(),
        popoverSize: {
          width: calendarRef.offsetWidth,
          height: calendarRef.offsetHeight,
        },
        viewport: { width: window.innerWidth, height: window.innerHeight },
      })
    )
  }, [fakeInputRef, calendarRef])

  // Measure the calendar once it is in the DOM, then place it. It stays hidden
  // until then so it never flashes at the wrong spot.
  useLayoutEffect(() => {
    if (!open) {
      setPosition(null)
      return
    }
    updatePosition()
  }, [open, updatePosition])

  useEffect(() => {
    if (!open) return

    // The form stays scrollable while the calendar is open, so the calendar
    // follows its field rather than drifting away from it. Its own scrolling
    // doesn't count, and it gives up once the field is out of sight.
    const handleScroll = event => {
      if (calendarRef && calendarRef.contains(event.target)) return
      if (!fakeInputRef) return

      if (isAnchorOffScreen(fakeInputRef.getBoundingClientRect(), {
        width: window.innerWidth,
        height: window.innerHeight,
      })) {
        closeCalendar()
      } else {
        updatePosition()
      }
    }

    // Stands in for the old full-screen backdrop, which closed the calendar on
    // an outside click but also swallowed every touch gesture, leaving the
    // form unscrollable on mobile. Clicks on the field itself fall through to
    // its own onClick, which toggles.
    const handlePointerDown = event => {
      if (calendarRef && calendarRef.contains(event.target)) return
      if (fakeInputRef && fakeInputRef.contains(event.target)) return
      closeCalendar()
    }

    window.addEventListener('resize', updatePosition)
    document.addEventListener('scroll', handleScroll, true)
    document.addEventListener('pointerdown', handlePointerDown, true)

    return () => {
      window.removeEventListener('resize', updatePosition)
      document.removeEventListener('scroll', handleScroll, true)
      document.removeEventListener('pointerdown', handlePointerDown, true)
    }
  }, [open, updatePosition, closeCalendar, calendarRef, fakeInputRef])

  const handleClear = useCallback(
    e => {
      e.stopPropagation()
      setDate(null)
      if (onChange) onChange(null)
    },
    [onChange]
  )

  return (
    <>
      <Wrapper>
        <Label style={{ marginBottom: '4px' }}>{label}</Label>
        <FakeInput
          ref={setFakeInputRef}
          theme={theme.palette}
          open={open ? 1 : 0}
          disabled={disabled ? 1 : 0}
          onClick={toggleCalendar}
          outlineColor={outlineColor}
        >
          <Typography
            fontSize="14px"
            lineHeight="14px"
            color={disabled ? 'rgba(0, 0, 0, 0.38)' : 'inherit'}
          >
            {date ? format(date, labelFormat) : labelFormat.toLowerCase()}
          </Typography>
          {date && !disabled && (
            <div
              style={{
                position: 'absolute',
                right: '24px',
                cursor: 'pointer',
                color: 'rgba(0, 0, 0, 0.54)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': {
                  color: 'rgba(0, 0, 0, 0.87)',
                },
              }}
              onClick={handleClear}
            >
              <ClearIcon fontSize="small" />
            </div>
          )}
          <ArrowDown theme={theme.palette} disabled={disabled ? 1 : 0} />
        </FakeInput>
        {error && <Typography variant="error">{error}</Typography>}
      </Wrapper>
      {open &&
        createPortal(
          <>
            <CalendarWrapper
              ref={setCalendarRef}
              theme={theme.palette}
              style={{
                top: position ? position.top : 0,
                left: position ? position.left : 0,
                visibility: position ? 'visible' : 'hidden',
                zIndex: 1301,
              }}
            >
              {isBasicCalendar ? (
                <BasicCalendar
                  date={date}
                  onSelect={selectedDate => {
                    setDate(selectedDate)
                    closeCalendar()
                    if (onChange) onChange(selectedDate)
                  }}
                />
              ) : (
                <MiniCalendar
                  isGrayWhenFull
                  date={date}
                  hasOccupancy={hasOccupancy}
                  onSelect={selectedDate => {
                    setDate(selectedDate)
                    closeCalendar()
                    if (onChange) onChange(selectedDate)
                  }}
                />
              )}
            </CalendarWrapper>
          </>,
          document.body
        )}
    </>
  )
}

export default SelectCalendar
